import React, { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger, SplitText } from 'gsap/all';
import image1 from './../assets/image/1.png';
import image2 from './../assets/image/2.png';
import image3 from './../assets/image/3.png';
import image4 from './../assets/image/4.png';
import image5 from './../assets/image/5.png';
import image6 from './../assets/image/Frame 36669.png';
import { useLanguage } from '../contexts/LanguageContext';

gsap.registerPlugin(ScrollTrigger, SplitText);

function ScrollWrap() {
  const { language } = useLanguage();

  const initStickyScroll = () => {
    // Clear existing ScrollTriggers for this component to prevent conflicts
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars && (trigger.vars.trigger === ".scroll_height" || trigger.vars.trigger === ".scroll_wrap")) {
        trigger.kill();
      }
    });

    // Ensure DOM elements exist before proceeding
    const scrollWrap = document.querySelector(".scroll_wrap");
    const scrollHeight = document.querySelector(".scroll_height");
    const scrollSection = document.querySelector(".scroll_section");
    
    if (!scrollWrap || !scrollHeight || !scrollSection) {
      return;
    }

    ScrollTrigger.create({
      trigger: ".scroll_height",
      pin: ".scroll_section",
      start: "top top",
      end: "bottom bottom",
      scrub: 1, // Increased from 0.5 for smoother scrolling
      anticipatePin: 1,
      invalidateOnRefresh: true
    });

    let e = gsap.utils.toArray(".scroll_sentence");
    let t = gsap.utils.toArray(".scroll_images_group");

    // Only proceed if elements exist
    if (e.length === 0 || t.length === 0) {
      return;
    }

    let o = e.map((sentence => new SplitText(sentence, {
      type: "words"
    })));

    gsap.set(e, {
      opacity: 0
    });

    gsap.set(e[0], {
      opacity: 1
    });

    t.forEach((imageGroup => {
      let images = imageGroup.querySelectorAll("img");
      gsap.set(images, {
        opacity: 0
      });
      gsap.set(images[0], {
        opacity: 1
      });
    }));

    o.forEach((splitText, index) => {
      let timeline = gsap.timeline({
        scrollTrigger: {
          trigger: ".scroll_height",
          start: () => `top+=${index * window.innerHeight} top`,
          end: () => `top+=${(index + 1) * window.innerHeight} top`,
          scrub: 0.5
        }
      });

      // Show current paragraph
      if (index > 0) {
        // Hide previous paragraph first
        timeline.to(e[index - 1], {
          opacity: 0,
          duration: 0.3
        }, "start");

        timeline.to(splitText.words, {
          opacity: 0,
          y: -20,
          stagger: 0.1,
          duration: 0.3
        }, "start");
      }

      // Show current paragraph
      timeline.to(e[index], {
        opacity: 1,
        duration: 0.3
      }, index > 0 ? "start+=0.3" : "start");

      timeline.from(splitText.words, {
        opacity: 0,
        y: 20,
        stagger: 0.1,
        duration: 0.5
      }, index > 0 ? "start+=0.4" : "start");
      
      // Handle image transitions
      t.forEach((imageGroup) => {
        let images = imageGroup.querySelectorAll("img");

        if (images.length > index) {
          // Hide previous image
          if (index > 0 && images[index - 1]) {
            timeline.to(images[index - 1], {
              opacity: 0,
              duration: 0.5
            }, "start");
          }

          // Show current image
          timeline.to(images[index], {
            opacity: 1,
            duration: 0.5
          }, index > 0 ? "start+=0.3" : "start");
        }
      });
    });
  };

  useEffect(() => {
    // Use requestAnimationFrame for better timing with DOM updates
    let rafId;
    let timeoutId;

    const initWithDelay = () => {
      // Ensure DOM is fully ready
      timeoutId = setTimeout(() => {
        rafId = requestAnimationFrame(() => {
          initStickyScroll();
          // Refresh ScrollTrigger after initialization
          ScrollTrigger.refresh();
        });
      }, 150);
    };

    initWithDelay();

    // Handle resize events for better responsiveness
    const handleResize = () => {
      ScrollTrigger.refresh();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      // Cleanup
      if (timeoutId) clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      
      // Clean up scroll triggers on unmount
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.vars && (trigger.vars.trigger === ".scroll_height" || trigger.vars.trigger === ".scroll_wrap")) {
          trigger.kill();
        }
      });
    };
  }, []);

  return (
    <header className="scroll_wrap">
      <div className="u-embed-css w-embed"></div>
      <div className="scroll_height">
        <div className="scroll_section u-vflex-center-center">

          <div className="scroll_images u-cover-absolute">
            <div className="scroll_images_group is-1">
              <img src={image1} loading="lazy" alt="Boat on water in Verse World" className="scroll_image" />
              <img src={image4} loading="lazy" alt="Person on hoverboard in Verse World" className="scroll_image" />
              <img src={image3} loading='lazy'  alt="on hoverboard in Verse World" className="scroll_image" />
              <img
                src={image4}
                loading="lazy"
                sizes="100vw"
                srcSet={`${image4.replace('.webp', '-p-500.webp')} 500w,
                        ${image4.replace('.webp', '-p-800.webp')} 800w,
                        ${image4.replace('.webp', '-p-1080.webp')} 1080w,
                        ${image4} 1440w`}
                alt="BYD in Verse World"
                className="scroll_image"
              />
              <img src={image2} loading="lazy" alt="Boat on water in Verse World" className="scroll_image" />
            </div>
            <div className="scroll_images_group is-4">
              <img src={image3} loading="lazy" alt="Person on hoverboard in Verse World" className="scroll_image" />
              <img src={image6} loading="lazy" alt="Interior design of apartment in Verse World" className="scroll_image" />
              <img src={image5} loading="lazy" alt="Person in office space in Verse World" className="scroll_image" />
              <img src={image4} loading='lazy' alt="Modern workspace design" className="scroll_image" />

            </div>
            <div className="scroll_images_group is-3">
              <img src={image2} loading="lazy" alt="Person in office space in Verse World" className="scroll_image" />
              <img src={image5} loading="lazy" alt="Boat on water in Verse World" className="scroll_image" />
              <img src={image3} loading='lazy' alt="Creative workspace environment" className="scroll_image" />
              <img src={image4} loading='lazy' alt="Professional office setup" className="scroll_image" />

            </div>
          </div>

          
          <div className="scroll_content">
            <div className="scroll_sentence">
              <h4 className="scroll_title u-text-style-h3">
                {language === 'ar' ? (
                  <>
                    في عالم مليء بالضوضاء اللانهائية، يقطع <span style={{color: '#9370f2'}}>لخص</span> الطريق بسهولة. يقدم ذكاؤنا الاصطناعي نصوص <span style={{color: '#9370f2'}}>دقيقة بنسبة 99%+</span> ويحدد التفاصيل الحاسمة والتواريخ والمواعيد النهائية والأهداف المهمة في <span style={{color: '#9370f2'}}>دقائق معدودة</span>.
                  </>
                ) : (
                  <>
                    In a world of endless noise, <span style={{color: '#9370f2'}}>Audiosum</span> cuts through with ease. Our AI delivers <span style={{color: '#9370f2'}}>99%+ accurate</span> transcripts and pinpoints critical details, dates, deadlines, and milestones in <span style={{color: '#9370f2'}}>minutes</span>.
                  </>
                )}
              </h4>
            </div>
            <div className="scroll_sentence">
              <h4 className="scroll_title u-text-style-h3">
                {language === 'ar' ? (
                  'سواء كنت طالباً أو مهنياً مشغولاً، حول المحتوى الخاص بك إلى رؤى قابلة للتنفيذ بسرعة وسهولة.'
                ) : (
                  'Whether you\'re a student or a busy professional, transform your content into actionable insights quickly and effortlessly.'
                )}
              </h4>
            </div>
          </div>
          <div className="scroll_actions u-hflex-center-center">
            <div className="u-display-contents">
              <div className="u-btn-group">
                <a 
                  href={`https://audiosum.app/signup?lang=${language}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-lg px-5"
                  style={{ 
                    fontSize: '1.1rem',
                    borderRadius: '25px',
                    background: '#0695FF',
                    border: 'none',
                    padding: '14px 40px',
                    boxShadow: '0 8px 30px rgba(6, 149, 255, 0.4)',
                    color: 'white',
                    fontWeight: '600',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#0578D6';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(6, 149, 255, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#0695FF';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(6, 149, 255, 0.4)';
                  }}
                >
                  {language === 'ar' ? 'اذهب إلى التطبيق' : 'Go to app'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default ScrollWrap;